/**
 * Time Validator Utility
 *
 * Handles time and date validation for scheduling forms.
 * Extracted from AvailabilityConfigurationModal for reusability.
 */
export const timeValidator = {
    /**
     * Parse a time string (HH:MM format) into components
     * @param time - Time string in HH:MM format
     * @returns Parsed time object or null if invalid
     */
    parseTime(time) {
        if (!time || typeof time !== 'string')
            return null;
        const parts = time.split(':');
        if (parts.length !== 2)
            return null;
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        if (isNaN(hours) || isNaN(minutes))
            return null;
        if (hours < 0 || hours > 23)
            return null;
        if (minutes < 0 || minutes > 59)
            return null;
        return {
            hours,
            minutes,
            totalMinutes: hours * 60 + minutes,
        };
    },
    /**
     * Check if end time is after start time
     * @param startTime - Start time in HH:MM format
     * @param endTime - End time in HH:MM format
     * @returns true if endTime > startTime
     */
    isEndTimeAfterStart(startTime, endTime) {
        const start = this.parseTime(startTime);
        const end = this.parseTime(endTime);
        if (!start || !end)
            return false;
        return end.totalMinutes > start.totalMinutes;
    },
    /**
     * Check if end date is after or equal to start date
     * @param startDate - Start date in YYYY-MM-DD format
     * @param endDate - End date in YYYY-MM-DD format
     * @returns true if endDate >= startDate
     */
    isEndDateAfterOrEqual(startDate, endDate) {
        if (!startDate || !endDate)
            return false;
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (isNaN(start.getTime()) || isNaN(end.getTime()))
            return false;
        return end >= start;
    },
    /**
     * Check if a date string is valid
     * @param date - Date string in YYYY-MM-DD format
     * @returns true if valid date
     */
    isValidDate(date) {
        if (!date)
            return false;
        const parsed = new Date(date);
        return !isNaN(parsed.getTime());
    },
    /**
     * Check if a time string is valid HH:MM format
     * @param time - Time string
     * @returns true if valid HH:MM format
     */
    isValidTime(time) {
        return this.parseTime(time) !== null;
    },
    /**
     * Calculate duration in minutes between two times
     * @param startTime - Start time in HH:MM format
     * @param endTime - End time in HH:MM format
     * @returns Duration in minutes or null if invalid
     */
    getDurationMinutes(startTime, endTime) {
        const start = this.parseTime(startTime);
        const end = this.parseTime(endTime);
        if (!start || !end)
            return null;
        return end.totalMinutes - start.totalMinutes;
    },
    /**
     * Calculate duration in hours between two times
     * @param startTime - Start time in HH:MM format
     * @param endTime - End time in HH:MM format
     * @returns Duration in hours (decimal) or null if invalid
     */
    getDurationHours(startTime, endTime) {
        const minutes = this.getDurationMinutes(startTime, endTime);
        if (minutes === null)
            return null;
        return minutes / 60;
    },
    /**
     * Format time for display (ensures HH:MM format)
     * @param time - Time string
     * @returns Formatted time string or empty string if invalid
     */
    formatTime(time) {
        const parsed = this.parseTime(time);
        if (!parsed)
            return '';
        const hours = String(parsed.hours).padStart(2, '0');
        const minutes = String(parsed.minutes).padStart(2, '0');
        return `${hours}:${minutes}`;
    },
};
